import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import {
  useState, useEffect, useCallback, useRef,
} from 'react';
import { useDispatch } from 'react-redux';
import { FiCopy, FiCheck } from 'react-icons/fi';
import { toastSuccess, toastError } from '../utils/toasts';
import {
  clearReservation,
  useGetOrderByCustomIdQuery,
  useGetPaymentByIdQuery,
  useSendOrderEmailMutation,
} from '../store';
import { PaymentStatus } from '../models/payment';
import CreateOrderSuccessSkeleton from '../components/CreateOrderSuccessSkeleton';
import { formatCurrency } from '../utils/shared';
import Button from '../components/atoms/Button';
import Accordion from '../components/atoms/Accordion';
import Carrousel from '../components/atoms/Carrousel/Carrousel';
import { OrderDTO } from '../models/order';
import AdminSectionTitle from '../components/AdminSectionTitle';
import getOrderOwnerReferralLink from '../utils/getOrderOwnerReferralLink';
import { OrderSuccess, OrderTimeout } from '../components/icons';
import { parseFriendlyDate, parseFriendlyDateAndHour } from '../utils/dates';
import getCustomTicketLabel from '../utils/getCustomTicketLabel';
import BackgroundBannerImage from '../components/BackgroundBannerImage';
import generateWhatsappMessage from '../utils/generateWhatsappMessage';
import useEventPixel from '../hooks/useEventPixel';
import { trackPurchase } from '../utils/metaPixelTracking';

const CreateOrderSuccess: React.FC = () => {
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const customId = searchParams.get('id');
  const { data: order, isLoading } = useGetOrderByCustomIdQuery(
    { customId: customId || '' },
    { skip: !customId },
  );
  const isBackoffice = useLocation().pathname.includes('admin');
  const [sendEmail] = useSendOrderEmailMutation();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const inPerson = searchParams.get('inperson') === 'true';
  const cashierId = searchParams.get('cashier_id');
  const sessionId = searchParams.get('session_id');
  const sellerAnotherTicketPath = inPerson ? `/admin/${order?.event.business_id}/events/${order?.event.id}/inperson/${cashierId}/${sessionId}` : `/admin/${order?.event.business_id}/events/${order?.event.id}/orders/new/tickets`;
  const [copiedField, setCopiedField] = useState<'cbu' | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const ziloPaymentId = order?.payments?.[0]?.provider === 'ZILO' ? order.payments[0].id : null;
  const {
    data: polledPayment,
    refetch: refetchPayment,
  } = useGetPaymentByIdQuery(
    { paymentId: ziloPaymentId || '' },
    {
      skip: !ziloPaymentId,
      pollingInterval: 10_000,
    },
  );

  const ziloPaymentStatus = polledPayment?.status ?? order?.payments?.[0]?.status;
  const isZiloPaid = ziloPaymentStatus === PaymentStatus.APPROVED;

  const handleRefreshPayment = useCallback(async () => {
    if (!ziloPaymentId) {
      return;
    }
    setIsRefreshing(true);
    try {
      await refetchPayment();
    } finally {
      setTimeout(() => setIsRefreshing(false), 600);
    }
  }, [ziloPaymentId, refetchPayment]);

  const hasShownPaidToast = useRef(false);
  useEffect(() => {
    if (isZiloPaid && !hasShownPaidToast.current) {
      hasShownPaidToast.current = true;
      toastSuccess('¡Tu pago fue acreditado!');
    }
  }, [isZiloPaid]);

  useEffect(() => {
    if (!ziloPaymentId) {
      return undefined;
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetchPayment();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [ziloPaymentId, refetchPayment]);

  const isZiloTransfer = order?.payments?.[0]?.provider === 'ZILO' && !isZiloPaid;
  const ziloPendingAmount = polledPayment?.pending_amount ?? order?.payments?.[0]?.pending_amount ?? null;
  const ziloPaidAmount = polledPayment?.paid_amount ?? order?.payments?.[0]?.paid_amount ?? 0;
  const ziloHasPartialPayment = ziloPendingAmount !== null && ziloPendingAmount !== polledPayment?.paid_amount && ziloPendingAmount > 0 && ziloPaidAmount > 0;
  const ziloHasOverPayment = ziloPendingAmount !== null && ziloPendingAmount < 0;
  const ziloReferenceNumber = order?.payments?.[0]?.reference_number ?? null;
  const ziloCreatedAt = order?.payments?.[0]?.created_at
    ? new Date(order.payments[0].created_at)
    : null;
  const ziloDeadline = ziloCreatedAt && !Number.isNaN(ziloCreatedAt.getTime())
    ? new Date(ziloCreatedAt.getTime() + 24 * 60 * 60 * 1000)
    : null;

  // Initialize event-specific Meta Pixel (covers redirect-based payment flows)
  useEventPixel(order?.event ?? null, { enabled: !isBackoffice });

  const hasFiredPurchasePixel = useRef(false);

  useEffect(() => {
    if (!order || isBackoffice || isZiloTransfer || hasFiredPurchasePixel.current) {
      return undefined;
    }
    const timer = setTimeout(() => {
      trackPurchase({
        content_name: order.event.name,
        content_type: 'product',
        content_ids: Array.from(new Set(order.items.map((item) => item.ticket.id))),
        value: order.payments?.[0]?.paid_amount ?? 0,
        num_items: order.items.length,
      });
      hasFiredPurchasePixel.current = true;
    }, 500);
    return () => clearTimeout(timer);
  }, [order, isBackoffice, isZiloTransfer]);

  const copyToClipboard = useCallback(async (value: string, field: 'cbu') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => {
        setCopiedField((current) => (current === field ? null : current));
      }, 2000);
    } catch {
      toastError('No se pudo copiar al portapapeles');
    }
  }, []);

  useEffect(() => {
    if (order) {
      // si la orden se crea con mas de un pago, o sin pagos esto se rompe. no deberia ocurrir
      setTotalAmount(order.payments?.[0].paid_amount);
    }
  }, [order]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else {
      setIsButtonDisabled(false);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  useEffect(() => {
    if (order) {
      dispatch(clearReservation());
    }
  }, [dispatch, order]);

  useEffect(() => {
    if (order) {
      localStorage.removeItem(`${order?.event.id}_orderItemData`);
      localStorage.removeItem(`${order?.event.id}_selectedTickets`);
      localStorage.removeItem(`${order?.event.id}_coupon`);
      localStorage.removeItem(`${order?.event.id}_email`);
    }
  }, [order]);

  const handleResendEmail = async () => {
    if (!order || isButtonDisabled) {
      return;
    }

    try {
      setIsButtonDisabled(true);
      setCountdown(60);
      await sendEmail({
        businessId: order.event.business_id,
        eventId: order.event.id,
        customId: order.custom_id,
      });
    } catch (error) {
      console.error('Error sending email:', error);
      setIsButtonDisabled(false);
      setCountdown(0);
    }
  };

  const handleCopyReferralLink = () => {
    if (!order) {
      return;
    }

    const referralLink = getOrderOwnerReferralLink(order.event, order);
    if (referralLink) {
      navigator.clipboard
        .writeText(referralLink)
        .then(() => {
          toastSuccess('Link copiado al portapapeles');
        })
        .catch((err) => {
          console.error('Error al copiar al portapapeles: ', err);
          toastError('Error al copiar el link');
        });
    }
  };

  const handleSendWhatsApp = () => {
    if (!order) {
      return;
    }

    // Map order.items to OrderItemData format
    const orderItems = order.items.map((item) => ({
      name: item.name,
      last_name: item.last_name,
      person_id: item.person_id,
      ticket_id: item.ticket.id,
      ticket: item.ticket,
      ticket_extra: item.ticket.is_extra || false,
      seat_name: item.event_seat?.name,
      seat_id: item.event_seat?.id,
      audience_discount: item.audience_discount,
      audience_discount_id: item.audience_discount_id,
    }));

    const message = generateWhatsappMessage(order.event, order, orderItems, totalAmount);
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${order.event.phone}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const getSuccessMessage = (orders: OrderDTO[], unpaid: boolean) => {
    if (isBackoffice) {
      return orders.length > 1
        ? '¡Entradas vendidas con éxito!'
        : '¡Entrada vendida con éxito!';
    }
    if (isZiloTransfer) {
      return 'Transferí para confirmar tu compra';
    }
    return unpaid ? 'Tu compra aún no está finalizada' : `¡Compra de ${getCustomTicketLabel(orders?.[0].event, { plural: false, casing: 'lower' })} exitosa!`;
  };

  if (isLoading) {
    return <CreateOrderSuccessSkeleton />;
  }

  if (!order) {
    console.error('no order');
    return <div>Orden no encontrada</div>;
  }

  const unpaid = order.is_manual;

  let ticketCount = 0;
  let extraCount = 0;

  return (
    <>
      {!isBackoffice && <BackgroundBannerImage event={order.event} />}
      <div className="grid grid-cols-1 lg:grid-cols-11 my-6 lg:gap-12 lg:px-8 text-text-primary min-h-[calc(100vh-200px)]">
        {inPerson && (
          <div className='col-span-11'>
            <AdminSectionTitle
              title="Venta presencial"
              subtitle={`${order.event.name}`}
            />
          </div>
        )}
        {!inPerson && (
          <div className="col-span-4 flex flex-col gap-4 w-full mb-6">
            <Carrousel
              images={[{ url: order.event.banner_image || '', eventId: order.event.id || '' }]}
            />
            <div className='glass-surface text-center lg:text-left'>
              <h1 className='text-3xl font-medium'>{order.event.name}</h1>
              <span className='text-xl text-text-primary'>{parseFriendlyDate(order.event.date || '', true, true)}</span>
            </div>
          </div>
        )}
        <div className='col-span-7 flex flex-col gap-6'>
          <div className="flex flex-col md:flex-row gap-6 glass-surface p-3">
            {!unpaid && !isZiloTransfer && (
              <div className="flex items-center justify-center p-4">
                <OrderSuccess width={147} height={147} />
              </div>
            )}
            {(isZiloTransfer && !isBackoffice) && (
              <div className="flex items-center justify-center p-4">
                <OrderTimeout width={147} height={147} />
              </div>
            )}
            {unpaid && !isZiloTransfer && !isBackoffice && (
              <div className="flex items-center justify-center p-4">
                <OrderTimeout width={147} height={147} />
              </div>
            )}
            {((unpaid && isBackoffice) || (isZiloTransfer && isBackoffice)) && (
              <div className="flex items-center justify-center p-4">
                <OrderSuccess width={147} height={147} />
              </div>
            )}
            <div className="flex flex-col gap-4 p-2 justify-center flex-start text-center md:text-left">
              <h1>{getSuccessMessage([order], unpaid)}</h1>
              {unpaid && !isBackoffice && (
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-3">
                    <p className="text-text-primary">
                      Para completar la compra por transferencia necesitamos enviarte los datos de pago por WhatsApp.
                    </p>
                    <p className="text-text-primary">
                      Si no nos escribís por WhatsApp, las entradas no se reservan.
                    </p>
                  </div>
                  <div className="w-fit mx-auto lg:mx-0">
                    <Button
                      variant="primary"
                      onClick={handleSendWhatsApp}
                    >
                      Enviar mensaje por WhatsApp
                    </Button>
                  </div>
                </div>
              )}
              <div>
                <p>
                  Número de orden: <strong> #{order.display_id} </strong>
                </p>
              </div>
              {!unpaid && !isZiloTransfer && (
                <div>
                <p>
                  Tu compra de tickets para {order.event.name}" fue procesada con éxito.
                  </p>
                </div>
              )}
            </div>
          </div>

          {isZiloTransfer && !isBackoffice && (
            <div className="glass-surface p-5 flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <h2 className="text-lg font-semibold text-text-primary">
                  Datos para la transferencia
                </h2>
                <p className="text-sm text-text-secondary">
                  Realizá una transferencia bancaria a los siguientes datos para confirmar tu compra.
                </p>
              </div>

              <div className="flex items-start gap-2 rounded-xl bg-warning-100 px-3 py-2.5">
                <span className="text-warning-600 text-base leading-none mt-0.5">&#9888;</span>
                <p className="text-sm">
                  <strong>Importante:</strong> El CVU es único para esta transacción. No transfieras a otro número y asegurate de que el monto sea <strong>exacto</strong>.
                </p>
              </div>

              <div className="flex flex-col gap-3 rounded-2xl border border-border-primary bg-surface-base p-4">
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-medium uppercase tracking-wide text-text-tertiary">Destinatario</span>
                  <span className="text-base font-semibold text-text-primary">Avantti SRL</span>
                </div>

                <div className="border-t border-border-secondary" />

                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-medium uppercase tracking-wide text-text-tertiary">Monto a transferir</span>
                  <span className="text-base font-semibold text-text-primary">{formatCurrency(totalAmount)}</span>
                </div>

                <div className="border-t border-border-secondary" />

                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium uppercase tracking-wide text-text-tertiary">CVU</span>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-semibold text-text-primary tracking-wider break-all flex-1 font-mono">
                      {ziloReferenceNumber || '-'}
                    </span>
                    {ziloReferenceNumber && (
                      <button
                        type="button"
                        className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition-colors ${
                          copiedField === 'cbu'
                            ? 'bg-success-100 text-success-600'
                            : 'bg-primary-100 text-primary-600 hover:bg-primary-200'
                        }`}
                        onClick={() => copyToClipboard(ziloReferenceNumber, 'cbu')}
                      >
                        {copiedField === 'cbu' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                        {copiedField === 'cbu' ? 'Copiado' : 'Copiar CVU'}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {ziloHasPartialPayment && (
                <div className="flex items-start gap-2 rounded-xl bg-warning-100 px-3 py-2.5">
                  <span className="text-warning-600 text-base leading-none mt-0.5">&#9888;</span>
                  <p className="text-sm text-text-secondary">
                    Recibimos un pago parcial. Aún te falta transferir <strong>{formatCurrency(ziloPendingAmount)}</strong> para completar tu compra.
                  </p>
                </div>
              )}

              {ziloHasOverPayment && (
                <div className="flex items-start gap-2 rounded-xl bg-info-100 px-3 py-2.5">
                  <span className="text-info-600 text-base leading-none mt-0.5">&#8505;</span>
                  <p className="text-sm text-text-secondary">
                    Transferiste <strong>{formatCurrency(Math.abs(ziloPendingAmount))}</strong> de más. Vamos a reembolsarte el monto excedente.
                  </p>
                </div>
              )}

              {!ziloHasPartialPayment && !ziloHasOverPayment && (
                <div className="flex items-start gap-2 rounded-xl px-3 py-2.5">
                  <span className="text-base leading-none mt-0.5">&#9200;</span>
                  <p className="text-sm">
                    Tenés <strong>24 horas</strong> para realizar la transferencia.
                    {ziloDeadline && <> Fecha límite: <strong>{parseFriendlyDateAndHour(ziloDeadline.toISOString(), true, true, true)} hs</strong>.</>}
                  </p>
                </div>
              )}

              <Button
                onClick={handleRefreshPayment}
                loading={isRefreshing}
                variant='secondary'
              >
                {isRefreshing ? 'Consultando...' : 'Ya transferí, verificar pago'}
              </Button>
            </div>
          )}

          <div className='flex flex-col gap-6'>
            <div className="flex flex-col gap-6 w-full">
              <div className="flex flex-col gap-6 w-full">
                <div className='glass-surface p-3'>
                  {order.items.map((item, index) => {
                    if (!item.ticket.is_extra) {
                      ticketCount += 1;
                    } else {
                      extraCount += 1;
                    }
                    return (
                      <div className="flex flex-col" key={index}>
                        <div className="my-2">
                          <div>
                            <span className="font-medium">
                              {!item.ticket.is_extra && <span>Entrada {ticketCount} - {item.ticket.name} {item.event_seat ? `/ ${item.event_seat.name}` : ''}</span>}
                              {item.ticket.is_extra && <span> Extra {extraCount} - {item.ticket.name}</span>}
                            </span>{' '}
                            <span
                              className={`${order.coupon_id || item.audience_discount_id ? 'line-through text-xs' : ''}`}
                            >
                            </span>
                            {(order.coupon_id || item.audience_discount_id) && (
                              <span className="text-sm text-text-primary">
                                {' '}
                              </span>
                            )}
                          </div>
                          {order.event.require_id_tickets && (
                            <div>
                              {item.name} {item.last_name} - {item.person_id}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {inPerson && <div className='glass-surface'>
                  <p>
                    Total: {formatCurrency(totalAmount)}
                  </p>
                </div>}
              </div>
            </div>

            {
              !isBackoffice && (
                <div className="flex flex-col gap-6 w-full">
                  <div className="glass-surface text-center rounded-3xl w-full p-3">
                    {isZiloTransfer && totalAmount > 0 && (
                      <p>
                        Una vez que realices la transferencia y se acredite el pago, te llegará un correo con tus entradas.
                      </p>
                    )}
                    {!isZiloTransfer && !order.is_manual && totalAmount > 0 && (order.status === 'paid' || isZiloPaid) && (
                      <p>
                        ¡Procesamos tu pago con éxito! En breve te llegará un correo con tus entradas.
                      </p>
                    )}
                    {!isZiloTransfer && !order.is_manual && totalAmount > 0 && order.status !== 'paid' && !isZiloPaid && (
                      <p>
                        Estamos procesando tu pago. Una vez que sea aprobado te llegará un correo con tus entradas.
                      </p>
                    )}
                    {totalAmount === 0 && (
                      <p>
                        Se están procesando los tickets correspondientes. En breve serán enviados a tu correo electrónico.
                      </p>
                    )}
                    {order.is_manual && totalAmount > 0 && (
                      <p>
                        Una vez que hayas mandado el mensaje por WhatsApp y la productora confirme tu pago, te llegará un correo con tus entradas.
                      </p>
                    )}
                  </div>
                </div>
              )
            }
          </div>

          {
            isBackoffice && (
              <div className="flex flex-col md:flex-row gap-4">
                {!inPerson && <Button variant="secondary" onClick={() => navigate(`/admin/${order.event.business_id}/events/${order.event.id}`)} >Volver a la vista del evento</Button>}
                <Button variant="primary" onClick={() => navigate(sellerAnotherTicketPath, { replace: true })}>Vender otra entrada</Button>
              </div>
            )
          }
          {
            !isBackoffice && (
              <div className="flex flex-col gap-6">
                {order.status === 'paid' && (
                  <div className="items-center justify-center w-full">
                    <Accordion title="¿No recibiste tus entradas?" padding='p-3'>
                      <div>
                        <p className="font-thin">
                          Es posible que no tengas espacio para recibir nuevos correos. Liberá espacio y envialo nuevamente.
                        </p>
                        <div className="flex justify-center mt-5 text-center mb-2 w-fit mx-auto">
                          <Button
                            onClick={handleResendEmail}
                            disabled={isButtonDisabled}
                            variant="primary"
                          >
                            {isButtonDisabled
                              ? `Reenviar correo (${countdown}s)`
                              : 'Reenviar correo'}
                          </Button>
                        </div>
                      </div>
                    </Accordion>
                  </div>
                )}
              </div>
            )
          }
          {!isBackoffice && order.owner_referral && order.status === 'paid' && (
            <div className="glass-surface">
              <div className="flex flex-col items-center text-center gap-4">
                <div>
                  <h2 className="text-2xl font-semibold text-primary-500 mb-2">¡Invitá a tus amigos y te regalamos tu entrada!</h2>
                  <p className="text-text-primary mb-2">Invitá a tus amigos a este evento con tu link.</p>
                  <p className="text-text-primary mb-4">Cada vez que alguien compre con tu link, recuperás parte de tu entrada.</p>
                  <p className="text-text-primary font-medium">Con 10 compras, la primer entrada de tu compra te sale gratis.</p>
                </div>
                <div className="w-full max-w-md">
                  <div className="flex items-center bg-surface-base border border-border-primary rounded-3xl p-3 gap-3">
                    <input
                      type="text"
                      value={getOrderOwnerReferralLink(order.event, order) || ''}
                      readOnly
                      className="flex-1 bg-transparent text-text-secondary text-sm outline-none"
                    />
                    <div
                      onClick={handleCopyReferralLink}
                      className="px-4 py-2 cursor-pointer"
                    >
                      <FiCopy size={20} />
                    </div>
                  </div>
                </div>
                <p >Encontrá todos los detalles en tu mail de resumen de compra</p>
              </div>
            </div>
          )}
          {!isBackoffice && <>
            <div className='flex flex-col gap-4 w-full glass-surface p-3'>
              <h2 className='text-lg font-medium text-text-primary text-center'>¿Querés ver mas sobre este o más eventos?</h2>
              <div className='flex gap-4 max-w-lg mx-auto w-full'>
                <Button variant="secondary" onClick={() => navigate(`/events/${order.event.slug}`)}>Volver al evento</Button>
                <Button variant="primary" onClick={() => navigate('/')}>Página principal</Button>
              </div>
              <p className="px-3 text-sm text-center text-text-secondary">
                Si tenés alguna duda podés comunicarte a nuestro{' '}
                <a
                  className="underline"
                  href="https://wa.me/542645515045"
                  target="_blank"
                >
                  WhatsApp
                </a>
                .
              </p>
            </div>
          </>}
        </div>
      </div>
    </>
  );
};

export default CreateOrderSuccess;
